'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMarketplaceStore } from '@/lib/marketplace/store';
import type { MarketplaceListing } from '@/lib/marketplace/types';
import { ReviewSection } from './review-section';
import { InstallDialog } from './install-dialog';
import {
  Star,
  Download,
  CheckCircle2,
  ExternalLink,
  ArrowLeft,
  Calendar,
  Tag,
  Link2,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

interface ListingDetailProps {
  slug: string;
}

const TYPE_LABELS: Record<string, string> = {
  agent_template: 'Agent Template',
  connector: 'Connector',
  skill: 'Skill',
  workflow: 'Workflow',
};

export function ListingDetail({ slug }: ListingDetailProps) {
  const navigate = useNavigate();
  const {
    currentListing,
    listingsLoading,
    listingsError,
    myInstalls,
    loadListing,
  } = useMarketplaceStore();

  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  useEffect(() => {
    loadListing(slug);
  }, [slug, loadListing]);

  const isInstalled = myInstalls.some((i) => i.listing_id === currentListing?.id);

  if (listingsLoading && !currentListing) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (listingsError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="size-4" />
        <AlertDescription>{listingsError}</AlertDescription>
      </Alert>
    );
  }

  if (!currentListing) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Listing not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/marketplace' })}>
          Back to Marketplace
        </Button>
      </div>
    );
  }

  const listing = currentListing;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/marketplace' })}>
        <ArrowLeft className="size-4 mr-2" />
        Back to Marketplace
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Icon/Banner */}
        <div className="shrink-0">
          {listing.icon_url ? (
            <img
              src={listing.icon_url}
              alt={listing.name}
              className="size-24 md:size-32 rounded-xl object-cover"
            />
          ) : (
            <div className="size-24 md:size-32 rounded-xl bg-primary/10 flex items-center justify-center text-4xl font-bold text-primary">
              {listing.name.charAt(0)}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary">
                  {TYPE_LABELS[listing.listing_type]}
                </Badge>
                {listing.publisher_verified && (
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="size-3" />
                    Verified
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl font-bold">{listing.name}</h1>
              <p className="text-muted-foreground mt-1">
                by {listing.publisher_name}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {isInstalled ? (
                <Button variant="secondary" disabled>
                  <CheckCircle2 className="size-4 mr-2" />
                  Installed
                </Button>
              ) : (
                <Button onClick={() => setInstallDialogOpen(true)}>
                  <Download className="size-4 mr-2" />
                  {listing.price_type === 'free' ? 'Install' : `Install - $${(listing.price_cents / 100).toFixed(2)}`}
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-1">
              <Star className="size-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium">{listing.rating_average.toFixed(1)}</span>
              <span className="text-muted-foreground">({listing.rating_count} reviews)</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Download className="size-4" />
              <span>{listing.install_count.toLocaleString()} installs</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="size-4" />
              <span>v{listing.version}</span>
            </div>
          </div>

          {/* Short Description */}
          <p className="text-muted-foreground">{listing.short_description}</p>

          {/* Tags */}
          {listing.tags && listing.tags.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="size-4 text-muted-foreground" />
              <div className="flex flex-wrap gap-1">
                {listing.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Screenshots */}
      {listing.screenshots && listing.screenshots.length > 0 && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {listing.screenshots.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Screenshot ${i + 1}`}
              className="h-48 rounded-lg object-cover"
            />
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="reviews">Reviews ({listing.rating_count})</TabsTrigger>
          <TabsTrigger value="versions">Versions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {listing.long_description || listing.short_description || 'No description provided.'}
                  </div>
                </CardContent>
              </Card>

              {listing.changelog && (
                <Card>
                  <CardHeader>
                    <CardTitle>What's New in v{listing.version}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      {listing.changelog}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Version</span>
                    <span>{listing.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category</span>
                    <span>{listing.category || 'Uncategorized'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Updated</span>
                    <span>
                      {listing.updated_at
                        ? new Date(listing.updated_at).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">License</span>
                    <span>{listing.price_type === 'free' ? 'Free' : 'Paid'}</span>
                  </div>
                </CardContent>
              </Card>

              {listing.required_connections && listing.required_connections.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Required Connections</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {listing.required_connections.map((conn) => (
                        <Badge key={conn} variant="secondary" className="gap-1">
                          <Link2 className="size-3" />
                          {conn}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <ReviewSection listingId={listing.id} canReview={isInstalled} />
        </TabsContent>

        <TabsContent value="versions" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">Version history coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Install Dialog */}
      <InstallDialog
        listing={listing}
        open={installDialogOpen}
        onClose={() => setInstallDialogOpen(false)}
      />
    </div>
  );
}
